import { useState, useEffect } from "react";

const API_BASE = "http://localhost:8000";

// ─── Framework Definitions ────────────────────────────────────────────────────
// Each control maps to an assessment category so we can derive compliance status
// from the customer's maturity scores.

const FRAMEWORKS = {
  glba: {
    id: "glba",
    name: "GLBA Safeguards Rule",
    shortName: "GLBA",
    version: "16 CFR Part 314 (2023)",
    color: "#3b82f6",
    description: "FTC Safeguards Rule requiring financial institutions to implement a comprehensive information security program.",
    controls: [
      { id: "314.4(a)", title: "Qualified Individual", description: "Designate a qualified individual to oversee, implement, and enforce the information security program.", category: "compliance_governance", threshold: 3 },
      { id: "314.4(b)", title: "Risk Assessment", description: "Conduct a risk assessment to identify foreseeable threats and the likelihood and potential damage of those threats.", category: "compliance_governance", threshold: 3 },
      { id: "314.4(c)(1)", title: "Access Controls", description: "Implement access controls, including MFA for any individual accessing customer information.", category: "identity_access", threshold: 3 },
      { id: "314.4(c)(2)", title: "Data Inventory", description: "Identify and classify customer information and the information systems storing or transmitting it.", category: "data_protection", threshold: 2 },
      { id: "314.4(c)(3)", title: "Encryption", description: "Encrypt customer information in transit and at rest.", category: "data_protection", threshold: 3 },
      { id: "314.4(c)(4)", title: "Secure Development", description: "Adopt secure development practices for in-house software used to transmit customer information.", category: "application_security", threshold: 2 },
      { id: "314.4(c)(5)", title: "Authentication", description: "Implement multi-factor authentication for systems containing customer information.", category: "identity_access", threshold: 3 },
      { id: "314.4(c)(6)", title: "Vulnerability Management", description: "Develop, implement, and maintain procedures for change management.", category: "vulnerability_management", threshold: 2 },
      { id: "314.4(c)(7)", title: "Security Monitoring", description: "Implement controls to monitor and detect unauthorized access to customer information.", category: "logging_monitoring", threshold: 3 },
      { id: "314.4(c)(8)", title: "Patch Management", description: "Regularly test and monitor the effectiveness of key controls, systems and procedures.", category: "vulnerability_management", threshold: 3 },
      { id: "314.4(c)(9)", title: "Security Awareness", description: "Train staff to implement the information security program.", category: "security_awareness", threshold: 2 },
      { id: "314.4(f)", title: "Service Provider Oversight", description: "Oversee service providers by selecting those that maintain appropriate safeguards.", category: "third_party_risk", threshold: 2 },
      { id: "314.4(h)", title: "Incident Response", description: "Establish a written incident response plan.", category: "incident_response", threshold: 3 },
      { id: "314.4(i)", title: "Board Reporting", description: "Report to the Board or governing body at least annually on the information security program.", category: "compliance_governance", threshold: 2 },
    ],
  },

  pcidss: {
    id: "pcidss",
    name: "PCI-DSS v4.0",
    shortName: "PCI-DSS",
    version: "v4.0 (March 2024)",
    color: "#f59e0b",
    description: "Payment Card Industry Data Security Standard for organizations handling cardholder data.",
    controls: [
      { id: "Req 1", title: "Network Security Controls", description: "Install and maintain network security controls.", category: "network_security", threshold: 3 },
      { id: "Req 2", title: "Secure Configurations", description: "Apply secure configurations to all system components.", category: "endpoint_protection", threshold: 3 },
      { id: "Req 3", title: "Cardholder Data Protection", description: "Protect stored account data.", category: "data_protection", threshold: 3 },
      { id: "Req 4", title: "Encryption in Transit", description: "Protect cardholder data with strong cryptography during transmission.", category: "data_protection", threshold: 3 },
      { id: "Req 5", title: "Malware Protection", description: "Protect all systems against malware.", category: "endpoint_protection", threshold: 3 },
      { id: "Req 6", title: "Secure Systems & Software", description: "Develop and maintain secure systems and software.", category: "application_security", threshold: 3 },
      { id: "Req 7", title: "Access Control — Need to Know", description: "Restrict access to system components and cardholder data by business need to know.", category: "identity_access", threshold: 3 },
      { id: "Req 8", title: "User Identification & Auth", description: "Identify users and authenticate access to system components.", category: "identity_access", threshold: 3 },
      { id: "Req 9", title: "Physical Access", description: "Restrict physical access to cardholder data.", category: "physical_security", threshold: 2 },
      { id: "Req 10", title: "Logging & Monitoring", description: "Log and monitor all access to system components and cardholder data.", category: "logging_monitoring", threshold: 3 },
      { id: "Req 11", title: "Security Testing", description: "Test security of systems and networks regularly.", category: "vulnerability_management", threshold: 3 },
      { id: "Req 12", title: "Policies & Programs", description: "Support information security with organizational policies and programs.", category: "compliance_governance", threshold: 3 },
    ],
  },

  sox: {
    id: "sox",
    name: "SOX ITGC",
    shortName: "SOX",
    version: "Sarbanes-Oxley Act — IT General Controls",
    color: "#ef4444",
    description: "IT General Controls supporting financial reporting integrity under Section 404.",
    controls: [
      { id: "AC-1", title: "Logical Access — User Provisioning", description: "Controls over granting, modifying, and revoking user access to financial systems.", category: "identity_access", threshold: 3 },
      { id: "AC-2", title: "Privileged Access Management", description: "Controls ensuring privileged accounts are appropriately restricted and monitored.", category: "identity_access", threshold: 3 },
      { id: "AC-3", title: "Segregation of Duties", description: "Controls preventing individuals from performing incompatible duties in financial systems.", category: "identity_access", threshold: 3 },
      { id: "CM-1", title: "Change Management", description: "Controls ensuring changes to financial systems are authorized, tested, and approved.", category: "vulnerability_management", threshold: 3 },
      { id: "CM-2", title: "Emergency Change Controls", description: "Controls over emergency/expedited changes to production financial systems.", category: "vulnerability_management", threshold: 2 },
      { id: "CO-1", title: "Job Scheduling & Operations", description: "Controls over batch processing and scheduled jobs affecting financial data.", category: "logging_monitoring", threshold: 2 },
      { id: "CO-2", title: "Incident Management", description: "Controls for identifying, logging, and resolving system incidents affecting financial data.", category: "incident_response", threshold: 2 },
      { id: "PD-1", title: "Systems Development", description: "Controls over the development and implementation of new financial systems.", category: "application_security", threshold: 2 },
      { id: "BR-1", title: "Data Backup & Recovery", description: "Controls ensuring financial data is backed up and can be restored.", category: "backup_recovery", threshold: 3 },
      { id: "IS-1", title: "Security Monitoring", description: "Controls for monitoring and detecting unauthorized access to financial systems.", category: "logging_monitoring", threshold: 3 },
      { id: "IS-2", title: "Antivirus & Patching", description: "Controls ensuring financial systems are protected from malware and vulnerabilities.", category: "endpoint_protection", threshold: 2 },
    ],
  },

  nist: {
    id: "nist",
    name: "NIST 800-53 Rev 5",
    shortName: "NIST",
    version: "SP 800-53 Rev 5 (2020)",
    color: "#22c55e",
    description: "Security and privacy controls for information systems and organizations, widely used as a baseline for federal and financial institution compliance.",
    controls: [
      { id: "AC", title: "Access Control Family", description: "Controls limiting information system access to authorized users and processes.", category: "identity_access", threshold: 3 },
      { id: "AT", title: "Awareness & Training", description: "Controls ensuring personnel are aware of security risks and trained accordingly.", category: "security_awareness", threshold: 2 },
      { id: "AU", title: "Audit & Accountability", description: "Controls creating, protecting, and retaining system audit records.", category: "logging_monitoring", threshold: 3 },
      { id: "CA", title: "Assessment, Authorization & Monitoring", description: "Controls for assessing security controls and authorizing systems for operation.", category: "compliance_governance", threshold: 3 },
      { id: "CM", title: "Configuration Management", description: "Controls establishing and maintaining baseline configurations.", category: "endpoint_protection", threshold: 2 },
      { id: "CP", title: "Contingency Planning", description: "Controls for establishing, maintaining, and testing response plans.", category: "business_continuity", threshold: 3 },
      { id: "IA", title: "Identification & Authentication", description: "Controls identifying and authenticating users, processes, and devices.", category: "identity_access", threshold: 3 },
      { id: "IR", title: "Incident Response", description: "Controls for establishing an incident response capability.", category: "incident_response", threshold: 3 },
      { id: "MA", title: "Maintenance", description: "Controls over maintenance performed on organizational systems.", category: "asset_management", threshold: 2 },
      { id: "MP", title: "Media Protection", description: "Controls protecting information on system media.", category: "data_protection", threshold: 2 },
      { id: "PE", title: "Physical & Environmental Protection", description: "Controls limiting physical access to organizational systems.", category: "physical_security", threshold: 2 },
      { id: "PL", title: "Planning", description: "Controls developing, documenting, and disseminating security plans.", category: "compliance_governance", threshold: 2 },
      { id: "RA", title: "Risk Assessment", description: "Controls for assessing risk to organizational operations.", category: "compliance_governance", threshold: 3 },
      { id: "SA", title: "System & Services Acquisition", description: "Controls allocating resources for information security.", category: "third_party_risk", threshold: 2 },
      { id: "SC", title: "System & Communications Protection", description: "Controls monitoring, controlling, and protecting communications.", category: "network_security", threshold: 3 },
      { id: "SI", title: "System & Information Integrity", description: "Controls identifying, reporting, and correcting system flaws.", category: "vulnerability_management", threshold: 3 },
    ],
  },
};

// ─── Compliance status derivation ─────────────────────────────────────────────

function deriveStatus(score, threshold) {
  if (!score && score !== 0) return "unknown";
  if (score >= threshold)         return "compliant";
  if (score >= threshold - 1)     return "partial";
  return "gap";
}

const STATUS_CONFIG = {
  compliant: { label: "Compliant",     color: "#22c55e", bg: "#22c55e15", icon: "✓" },
  partial:   { label: "Partial",       color: "#f59e0b", bg: "#f59e0b15", icon: "◐" },
  gap:       { label: "Gap",           color: "#ef4444", bg: "#ef444415", icon: "✕" },
  unknown:   { label: "Not Assessed",  color: "#475569", bg: "#47556915", icon: "?" },
};

function buildFrameworkData(framework, scores) {
  return framework.controls.map(ctrl => {
    const catData = scores[ctrl.category] || {};
    const score   = catData.score ?? null;
    const target  = catData.target ?? 5;
    const status  = deriveStatus(score, ctrl.threshold);
    return { ...ctrl, score, target, status };
  });
}

function calcComplianceScore(controls) {
  const assessed = controls.filter(c => c.status !== "unknown");
  if (!assessed.length) return null;
  const compliant = assessed.filter(c => c.status === "compliant").length;
  const partial   = assessed.filter(c => c.status === "partial").length;
  return Math.round(((compliant + partial * 0.5) / assessed.length) * 100);
}

// ─── Prompt builder ───────────────────────────────────────────────────────────

function buildRegulatoryPrompt(framework, controls, orgName) {
  const gaps     = controls.filter(c => c.status === "gap");
  const partials = controls.filter(c => c.status === "partial");
  const compliant = controls.filter(c => c.status === "compliant");
  const score    = calcComplianceScore(controls);

  return `You are a regulatory compliance specialist preparing a ${framework.name} compliance assessment for ${orgName}. Write a professional compliance report in clear prose — no markdown, no bullet points. Use full paragraphs. Tone: authoritative, specific, examiner-ready.

Framework: ${framework.name} (${framework.version})
Overall compliance score: ${score !== null ? score + "%" : "Insufficient data"}
Controls assessed: ${controls.length}
Compliant: ${compliant.length} | Partial: ${partials.length} | Gaps: ${gaps.length}

${gaps.length > 0 ? `Critical gaps: ${gaps.map(g => `${g.id} — ${g.title} (current maturity: ${g.score ?? "not assessed"}/${g.threshold} required)`).join("; ")}` : "No critical gaps identified."}

${partials.length > 0 ? `Partial compliance: ${partials.map(p => `${p.id} — ${p.title}`).join("; ")}` : ""}

Write exactly three sections:

COMPLIANCE OVERVIEW
2-3 sentences summarizing the organization's overall compliance posture against ${framework.shortName}. Be direct about the risk of examination findings.

KEY GAPS & REGULATORY EXPOSURE
2-3 sentences on the most serious compliance gaps, their specific regulatory citations, and the examination or enforcement risk they create for a financial institution.

REMEDIATION PRIORITIES
2-3 sentences on what must be addressed first to achieve compliance, in priority order. Be specific about which controls to tackle and why sequencing matters.

Use the exact section titles above. No markdown. Professional tone throughout.`;
}

// ─── Control Row ──────────────────────────────────────────────────────────────

function ControlRow({ ctrl, index }) {
  const sc = STATUS_CONFIG[ctrl.status];
  return (
    <tr style={{ ...sty.tr, animationDelay: `${index * 0.03}s` }}>
      <td style={sty.td}>
        <span style={sty.ctrlId}>{ctrl.id}</span>
      </td>
      <td style={sty.td}>
        <div style={sty.ctrlTitle}>{ctrl.title}</div>
        <div style={sty.ctrlDesc}>{ctrl.description}</div>
      </td>
      <td style={sty.td}>
        <span style={sty.catTag}>{ctrl.category?.replace(/_/g, " ")}</span>
      </td>
      <td style={{ ...sty.td, textAlign: "center" }}>
        {ctrl.score !== null
          ? <span style={sty.scoreVal}>{ctrl.score}<span style={sty.scoreReq}>/{ctrl.threshold}</span></span>
          : <span style={{ color: "#334155", fontSize: 12 }}>—</span>
        }
      </td>
      <td style={{ ...sty.td, textAlign: "center" }}>
        <span style={{ ...sty.statusPill, background: sc.bg, color: sc.color }}>
          {sc.icon} {sc.label}
        </span>
      </td>
    </tr>
  );
}

// ─── Compliance Score Ring ────────────────────────────────────────────────────

function ScoreRing({ score, color }) {
  if (score === null) return <div style={{ ...sty.ring, borderColor: "#1f2937" }}><span style={sty.ringVal}>N/A</span></div>;
  const ringColor = score >= 80 ? "#22c55e" : score >= 60 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ ...sty.ring, borderColor: ringColor, boxShadow: `0 0 20px ${ringColor}20` }}>
      <span style={{ ...sty.ringVal, color: ringColor }}>{score}%</span>
      <span style={sty.ringLabel}>compliant</span>
    </div>
  );
}

// ─── Framework Panel ──────────────────────────────────────────────────────────

function FrameworkPanel({ framework, scores, orgName }) {
  const controls  = buildFrameworkData(framework, scores);
  const score     = calcComplianceScore(controls);
  const gaps      = controls.filter(c => c.status === "gap").length;
  const partials  = controls.filter(c => c.status === "partial").length;
  const compliant = controls.filter(c => c.status === "compliant").length;

  const [narrative, setNarrative]   = useState("");
  const [narSections, setNarSections] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [narError, setNarError]     = useState(null);
  const [view, setView]             = useState("table"); // table | narrative

  const TITLES = ["COMPLIANCE OVERVIEW", "KEY GAPS & REGULATORY EXPOSURE", "REMEDIATION PRIORITIES"];
  const TITLE_COLORS = { "COMPLIANCE OVERVIEW": framework.color, "KEY GAPS & REGULATORY EXPOSURE": "#ef4444", "REMEDIATION PRIORITIES": "#22c55e" };

  function parseNarrative(text) {
    const sections = [];
    let remaining = text;
    for (let i = 0; i < TITLES.length; i++) {
      const idx = remaining.indexOf(TITLES[i]);
      if (idx === -1) continue;
      const start = idx + TITLES[i].length;
      const nextIdx = TITLES.slice(i + 1).reduce((best, t) => {
        const pos = remaining.indexOf(t, start);
        return pos !== -1 && (best === -1 || pos < best) ? pos : best;
      }, -1);
      const content = (nextIdx === -1 ? remaining.slice(start) : remaining.slice(start, nextIdx))
        .replace(/^[\s:—\-]+/, "").trim();
      if (content) sections.push({ title: TITLES[i], content });
      if (nextIdx !== -1) remaining = remaining.slice(nextIdx);
    }
    if (!sections.length && text.trim()) sections.push({ title: "ASSESSMENT", content: text.trim() });
    return sections;
  }

  const handleGenerate = async () => {
    setGenerating(true);
    setNarError(null);
    setView("narrative");
    try {
      const prompt = buildRegulatoryPrompt(framework, controls, orgName);
      const res = await fetch(`${API_BASE}/api/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, max_tokens: 1000 }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `API error ${res.status}`);
      }
      const data = await res.json();
      const text = data.content || "";
      setNarrative(text);
      setNarSections(parseNarrative(text));
    } catch (e) {
      setNarError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={sty.panel}>
      {/* Framework Header */}
      <div style={{ ...sty.fwHeader, borderLeftColor: framework.color }}>
        <div style={sty.fwHeaderLeft}>
          <div style={{ ...sty.fwBadge, background: framework.color + "15", color: framework.color, border: `1px solid ${framework.color}30` }}>
            {framework.shortName}
          </div>
          <div>
            <div style={sty.fwName}>{framework.name}</div>
            <div style={sty.fwVersion}>{framework.version}</div>
            <div style={sty.fwDesc}>{framework.description}</div>
          </div>
        </div>
        <div style={sty.fwHeaderRight}>
          <ScoreRing score={score} color={framework.color} />
          <div style={sty.fwStats}>
            <div style={{ ...sty.fwStat, color: "#22c55e" }}>{compliant} <span style={sty.fwStatLabel}>compliant</span></div>
            <div style={{ ...sty.fwStat, color: "#f59e0b" }}>{partials} <span style={sty.fwStatLabel}>partial</span></div>
            <div style={{ ...sty.fwStat, color: "#ef4444" }}>{gaps} <span style={sty.fwStatLabel}>gaps</span></div>
          </div>
        </div>
      </div>

      {/* View Toggle + Generate */}
      <div style={sty.viewBar}>
        <div style={sty.viewToggle}>
          {[["table","Control Mapping"],["narrative","AI Assessment"]].map(([v,label]) => (
            <button key={v}
              style={{ ...sty.viewBtn, ...(view === v ? { ...sty.viewBtnActive, borderBottomColor: framework.color, color: framework.color } : {}) }}
              onClick={() => setView(v)}>
              {label}
              {v === "narrative" && narSections.length > 0 && <span style={{ ...sty.readyDot, background: framework.color }} />}
            </button>
          ))}
        </div>
        <button
          style={{ ...sty.genBtn, borderColor: framework.color + "60", color: framework.color, opacity: generating ? 0.6 : 1, cursor: generating ? "not-allowed" : "pointer" }}
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? "⟳ Generating..." : `✦ Generate ${framework.shortName} Assessment`}
        </button>
      </div>

      {/* Control Mapping Table */}
      {view === "table" && (
        <div style={sty.tableWrap}>
          <table style={sty.table}>
            <thead>
              <tr>
                {["Control", "Requirement", "Category", "Score", "Status"].map(h => (
                  <th key={h} style={sty.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {controls.map((ctrl, i) => (
                <ControlRow key={ctrl.id} ctrl={ctrl} index={i} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AI Narrative */}
      {view === "narrative" && (
        <div style={sty.narrativePane}>
          {generating && (
            <div style={sty.genState}>
              <div style={{ ...sty.genIcon, color: framework.color }}>✦</div>
              <div style={sty.genText}>Generating {framework.shortName} compliance assessment...</div>
            </div>
          )}
          {narError && <div style={sty.narError}>⚠ {narError}</div>}
          {!generating && narSections.length === 0 && !narError && (
            <div style={sty.narEmpty}>
              Click "Generate {framework.shortName} Assessment" to produce an AI compliance narrative for this framework.
            </div>
          )}
          {narSections.map((s, i) => (
            <div key={i} style={{ ...sty.narSection, borderLeftColor: TITLE_COLORS[s.title] || framework.color }}>
              <div style={{ ...sty.narTitle, color: TITLE_COLORS[s.title] || framework.color }}>{s.title}</div>
              <div style={sty.narBody}>{s.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function RegulatoryReports() {
  const [scores, setScores]     = useState({});
  const [orgName, setOrgName]   = useState("Your Organization");
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState("glba");

  useEffect(() => {
    (async () => {
      try {
        const customerId = localStorage.getItem("customer_id") || "cust_techcorpin_575148";
        const res = await fetch(`${API_BASE}/api/assessments/current?customer_id=${customerId}`);
        if (res.ok) {
          const data = await res.json();
          const responses = data.assessment?.responses || data.responses || {};
          if (Object.keys(responses).length) setScores(responses);
          if (data.assessment?.organization_name) setOrgName(data.assessment.organization_name);
        }
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  const fwList = Object.values(FRAMEWORKS);
  const activeFramework = FRAMEWORKS[activeTab];

  if (loading) return (
    <div style={{ ...sty.page, display:"flex", alignItems:"center", justifyContent:"center", color:"#475569" }}>
      Loading assessment data...
    </div>
  );

  return (
    <div style={sty.page}>
      {/* Header */}
      <div style={sty.header}>
        <div>
          <div style={sty.breadcrumb}>Reports / Regulatory Compliance</div>
          <h1 style={sty.title}>Regulatory Compliance Reports</h1>
          <p style={sty.subtitle}>
            Control-by-control compliance mapping and AI-generated assessments for {orgName}
          </p>
        </div>
        <div style={sty.scoreSummary}>
          {fwList.map(fw => {
            const controls = buildFrameworkData(fw, scores);
            const score = calcComplianceScore(controls);
            return (
              <div key={fw.id} style={sty.miniScore} onClick={() => setActiveTab(fw.id)}>
                <div style={{ ...sty.miniScoreVal, color: score !== null ? (score >= 80 ? "#22c55e" : score >= 60 ? "#f59e0b" : "#ef4444") : "#334155" }}>
                  {score !== null ? `${score}%` : "—"}
                </div>
                <div style={{ ...sty.miniScoreLabel, color: fw.color }}>{fw.shortName}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Framework Tabs */}
      <div style={sty.tabBar}>
        {fwList.map(fw => (
          <button key={fw.id}
            style={{
              ...sty.tab,
              ...(activeTab === fw.id ? {
                color: fw.color,
                borderBottomWidth: "2px",
                borderBottomStyle: "solid",
                borderBottomColor: fw.color,
              } : {})
            }}
            onClick={() => setActiveTab(fw.id)}>
            {fw.shortName}
          </button>
        ))}
      </div>

      {/* Active Framework Panel */}
      <div style={sty.content}>
        <FrameworkPanel
          key={activeTab}
          framework={activeFramework}
          scores={scores}
          orgName={orgName}
        />
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const sty = {
  page: {
    minHeight: "100vh",
    background: "#07090e",
    color: "#e2e8f0",
    fontFamily: "'DM Sans', system-ui, sans-serif",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "30px 40px 22px", borderBottom: "1px solid #111827",
  },
  breadcrumb: { fontSize: 11, color: "#334155", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6, fontFamily: "'DM Mono', monospace" },
  title: { fontSize: 24, fontWeight: 800, color: "#f1f5f9", margin: "0 0 6px", letterSpacing: "-0.02em" },
  subtitle: { fontSize: 13, color: "#475569", margin: 0 },
  scoreSummary: { display: "flex", gap: 2 },
  miniScore: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
    padding: "10px 16px", cursor: "pointer",
    borderRadius: 8, transition: "background 0.15s",
  },
  miniScoreVal: { fontSize: 18, fontWeight: 800, lineHeight: 1 },
  miniScoreLabel: { fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" },
  tabBar: {
    display: "flex", borderBottom: "1px solid #111827",
    padding: "0 40px", background: "#05070c",
  },
  tab: {
    padding: "13px 22px", background: "none",
    border: "none",
    borderBottomWidth: "2px", borderBottomStyle: "solid", borderBottomColor: "transparent",
    color: "#475569", fontSize: 13, fontWeight: 700,
    cursor: "pointer", letterSpacing: "0.04em",
    fontFamily: "'DM Mono', monospace",
  },
  content: { padding: "28px 40px" },
  panel: { display: "flex", flexDirection: "column", gap: 0 },
  fwHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    background: "#0c0f1a", border: "1px solid #111827",
    borderLeft: "4px solid", borderRadius: "0 12px 0 0",
    padding: "24px 28px", gap: 20,
  },
  fwHeaderLeft: { display: "flex", gap: 16, alignItems: "flex-start", flex: 1 },
  fwBadge: {
    fontSize: 11, fontWeight: 800, letterSpacing: "0.1em",
    borderRadius: 6, padding: "4px 10px", flexShrink: 0,
    fontFamily: "'DM Mono', monospace",
  },
  fwName: { fontSize: 16, fontWeight: 800, color: "#f1f5f9", marginBottom: 3 },
  fwVersion: { fontSize: 11, color: "#475569", marginBottom: 5, fontFamily: "'DM Mono', monospace" },
  fwDesc: { fontSize: 12, color: "#64748b", lineHeight: 1.6, maxWidth: 480 },
  fwHeaderRight: { display: "flex", gap: 20, alignItems: "center", flexShrink: 0 },
  ring: {
    width: 76, height: 76, borderRadius: "50%",
    border: "3px solid", display: "flex",
    flexDirection: "column", alignItems: "center", justifyContent: "center",
  },
  ringVal: { fontSize: 18, fontWeight: 800, lineHeight: 1 },
  ringLabel: { fontSize: 9, color: "#475569", letterSpacing: "0.06em" },
  fwStats: { display: "flex", flexDirection: "column", gap: 6 },
  fwStat: { fontSize: 16, fontWeight: 800, lineHeight: 1 },
  fwStatLabel: { fontSize: 10, color: "#475569", fontWeight: 400 },
  viewBar: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "#09111a", border: "1px solid #111827", borderTop: "none",
    padding: "0 20px",
  },
  viewToggle: { display: "flex" },
  viewBtn: {
    padding: "11px 16px", background: "none",
    border: "none",
    borderBottomWidth: "2px", borderBottomStyle: "solid", borderBottomColor: "transparent",
    color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 6,
  },
  viewBtnActive: { },
  readyDot: { width: 6, height: 6, borderRadius: "50%" },
  genBtn: {
    padding: "7px 16px", background: "none",
    border: "1px solid", borderRadius: 7,
    fontSize: 12, fontWeight: 700, cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
  },
  tableWrap: {
    border: "1px solid #111827", borderTop: "none",
    borderRadius: "0 0 12px 12px", overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    fontSize: 10, fontWeight: 700, color: "#334155",
    letterSpacing: "0.08em", textTransform: "uppercase",
    padding: "10px 14px", textAlign: "left",
    background: "#080b12", borderBottom: "1px solid #111827",
    fontFamily: "'DM Mono', monospace",
  },
  tr: { borderBottom: "1px solid #0d1018" },
  td: { fontSize: 12, color: "#94a3b8", padding: "12px 14px", verticalAlign: "top" },
  ctrlId: { fontSize: 11, fontWeight: 700, color: "#64748b", fontFamily: "'DM Mono', monospace" },
  ctrlTitle: { fontSize: 13, fontWeight: 600, color: "#cbd5e1", marginBottom: 3 },
  ctrlDesc: { fontSize: 11, color: "#475569", lineHeight: 1.5 },
  catTag: {
    fontSize: 10, color: "#475569", background: "#111827",
    borderRadius: 4, padding: "2px 7px", display: "inline-block",
    fontFamily: "'DM Mono', monospace",
  },
  scoreVal: { fontSize: 14, fontWeight: 700, color: "#94a3b8", fontFamily: "'DM Mono', monospace" },
  scoreReq: { color: "#334155" },
  statusPill: {
    fontSize: 10, fontWeight: 700, borderRadius: 5,
    padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 4,
    fontFamily: "'DM Mono', monospace",
  },
  narrativePane: {
    border: "1px solid #111827", borderTop: "none",
    borderRadius: "0 0 12px 12px",
    padding: "32px 36px", background: "#0b0e18",
    display: "flex", flexDirection: "column", gap: 24,
  },
  genState: { display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0", gap: 12 },
  genIcon: { fontSize: 28 },
  genText: { fontSize: 14, color: "#64748b" },
  narError: { fontSize: 13, color: "#ef4444", background: "#1a0c0c", border: "1px solid #ef444330", borderRadius: 8, padding: "10px 14px" },
  narEmpty: { fontSize: 13, color: "#334155", textAlign: "center", padding: "40px 0" },
  narSection: { borderLeft: "3px solid", paddingLeft: 18 },
  narTitle: { fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8, fontFamily: "'DM Mono', monospace" },
  narBody: { fontSize: 14, color: "#94a3b8", lineHeight: 1.8 },
};
